/**
 * F 331 CORRECTIVE — THE TELEMETRY FACE.
 *
 * A reusable widget, not an AI chart. It knows about signals, retention and
 * estimates; it knows NOTHING about providers, models or capabilities. Anything
 * in the product that wants "the current reading, and the shape of the recent
 * past" mounts this and gets the same behaviour.
 *
 * THE ESTIMATE MARKER
 * ------------------
 * A bucket is marked with a `~` when ANY sample in it was estimated rather than
 * reported. F 330 established that a token count is exact when the provider
 * reports it and estimated when it comes from a local tokenizer — and the whole
 * point of carrying that distinction is refusing to blur it here. A greyed `~`
 * that means "this number is a guess" is more useful than a clean bar that lies.
 */
import { Card, Group, Stack, Text, Badge, Tooltip } from '@mantine/core';
import type { SignalState, TelemetrySinkLike } from '../../../core/telemetry/telemetrySink';

export interface TelemetryFaceProps {
  sink: TelemetrySinkLike;
  signals?: string[];
  title?: string;
  /** Rendered when a signal has no history — live-only is the default mode. */
  emptyHint?: string;
}

function bucketColor(estimated: boolean): string {
  return estimated ? '#868e96' : '#4c6ef5';
}

/** One signal: the live reading plus its retained history. */
export function TelemetrySeries({
  state,
  emptyHint,
}: {
  state: SignalState;
  emptyHint?: string;
}) {
  const max = Math.max(1, ...state.history.map((b) => Math.max(b.max, b.mean)));
  return (
    <Stack gap={4} data-testid={`telemetry-${state.signal}`} data-mode={state.mode}>
      <Group justify="space-between">
        <Text size="xs" ff="monospace">{state.signal}</Text>
        <Group gap={4}>
          {state.live?.countType === 'estimated-local' || state.live?.countType === 'unknown' ? (
            <Tooltip label="estimated locally — the provider did not report this">
              <Badge size="xs" variant="light" color="gray" data-testid="estimate-marker">~estimated</Badge>
            </Tooltip>
          ) : (
            <Badge size="xs" variant="light" color="blue" data-testid="exact-marker">exact</Badge>
          )}
          <Text size="xs" fw={700}>
            {state.live ? state.live.value : '—'}
          </Text>
        </Group>
      </Group>

      {state.history.length === 0 ? (
        <Text size="10px" c="dimmed" data-testid="telemetry-no-history">
          {emptyHint ?? `${state.mode} — no history retained`}
        </Text>
      ) : (
        <Group gap={2} align="flex-end" h={34} data-testid="telemetry-history">
          {state.history.map((b, i) => (
            <Tooltip key={b.from} withArrow label={
              <Text size="xs">
                {b.count} sample{b.count === 1 ? '' : 's'} · mean {b.mean.toFixed(2)}{b.estimated ? ' ~' : ''}
              </Text>
            }>
              <div
                style={{ width: 7, height: Math.max(2, (b.mean / max) * 32), background: bucketColor(b.estimated), borderRadius: 2 }}
                data-estimated={b.estimated ? 'true' : 'false'}
                data-count={b.count}
              />
            </Tooltip>
          ))}
        </Group>
      )}
    </Stack>
  );
}

/**
 * The face. Renders every signal it is given, or every signal the sink knows.
 */
export function TelemetryFace({ sink, signals, title, emptyHint }: TelemetryFaceProps) {
  const list = signals ?? sink.signals();
  const rows = list.map((s) => sink.read(s)).filter((s) => s.live !== null);
  return (
    <Card withBorder padding="md" data-testid="telemetry-face">
      {title && <Text fw={700} size="sm" mb={4}>{title}</Text>}
      {rows.length === 0 ? (
        <Text size="xs" c="dimmed" data-testid="telemetry-empty">no signal has been written yet</Text>
      ) : (
        <Stack gap="xs">
          {rows.map((s) => <TelemetrySeries key={s.signal} state={s} emptyHint={emptyHint} />)}
        </Stack>
      )}
    </Card>
  );
}